import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check } from "lucide-react";
import { HERO, EXPERIENCE, PATHWAYS, WHATS_ON, ANGLESEY, FOUNDER, BREATHWORK } from "../../content/home";
import { springText } from "../../pages/EventsList";
import { Reveal, SmartLink } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "BUILT ON *experience.*" -> sans caps with Lora italic lowercase words.
export const Mix = ({ text }) => text.split(/(\*[^*]+\*)/).map((p, i) =>
  p.startsWith("*") ? <span key={i} className="feral-italic">{p.slice(1, -1)}</span> : <span key={i}>{p}</span>);

const BTN = "inline-flex items-center justify-center gap-2 feral-nav uppercase tracking-[0.06em] text-[13px] md:text-[15px] h-[54px] md:h-[64px] px-8 md:px-10 transition-colors duration-300";
export const btn = {
  yellow: `${BTN} bg-[#ebc754] text-[#9b3d0a] hover:bg-[#f2e5de]`,
  outline: `${BTN} border border-[#f2e5de] text-[#f2e5de] hover:bg-[#f2e5de] hover:text-[#231f20]`,
  rust: `${BTN} bg-[#9b3d0a] text-[#ebc754] hover:bg-[#82254d]`,
  rustOutline: `${BTN} border border-[#9b3d0a] text-[#9b3d0a] hover:bg-[#9b3d0a] hover:text-[#ebc754]`,
};

const H2 = ({ text, className = "" }) => <h2 className={`feral-display text-[2.4rem] sm:text-5xl lg:text-[3.6rem] leading-[1.02] ${className}`}><Mix text={text} /></h2>;

export const Hero = () => (
  <section className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="home-hero">
    <video className="hidden md:block absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline preload="metadata" poster="/hero-v2-poster.jpg" aria-hidden="true" data-testid="hero-video">
      <source src="/hero-v2.webm" type="video/webm" />
      <source src="/hero-v2.mp4" type="video/mp4" />
    </video>
    <video className="md:hidden absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline preload="metadata" poster="/hero-v2-mobile-poster.jpg" aria-hidden="true" data-testid="hero-video-mobile">
      <source src="/hero-v2-mobile.webm" type="video/webm" />
      <source src="/hero-v2-mobile.mp4" type="video/mp4" />
    </video>
    <div className="absolute inset-0 bg-[#231f20]/55" />
    <div className="relative h-full flex flex-col items-center justify-center text-center px-6 pt-16">
      <h1 className="feral-display !font-bold text-[#ebc754] text-[3.4rem] sm:text-7xl lg:text-[6.2rem] leading-none" data-testid="hero-headline">
        <Mix text="*be* FREE. *be* FERAL." />
      </h1>
      <p className="mt-6 md:mt-8 text-[#f2e5de] text-base md:text-[1.45rem] leading-[1.6] max-w-[44rem]" data-testid="hero-copy">{HERO.copy}</p>
      <div className="mt-12 md:mt-28 flex flex-wrap justify-center gap-4">
        <SmartLink to="/#whats-on" className={btn.yellow} data-testid="hero-cta-events">Upcoming events</SmartLink>
        <SmartLink to="/venue-hire" className={btn.outline} data-testid="hero-cta-retreat">Plan a retreat</SmartLink>
      </div>
    </div>
  </section>
);

export const Intro = () => (
  <section className="bg-[#f2e5de] py-24 md:py-32 px-6 text-center" data-testid="section-experience">
    <Reveal>
      <p className="feral-nav uppercase tracking-[0.08em] text-xs text-[#9b3d0a] mb-6">The collective</p>
      <H2 text={"BUILT ON *experience.*\nROOTED IN *nature.*"} className="text-[#ebc754] whitespace-pre-line" />
      <p className="mt-10 mx-auto max-w-[34rem] text-[#231f20] text-base md:text-lg leading-[1.7]">{EXPERIENCE.paras[0]}</p>
      <img src="/skin/wave-yellow.svg" alt="" className="mx-auto mt-12 w-28" />
    </Reveal>
  </section>
);

export const OwnThem = () => (
  <section className="relative overflow-hidden py-28 md:py-40 px-6 text-center" data-testid="section-own-them">
    <img src="/skin/own-them.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="relative max-w-[46rem] mx-auto">
      <Reveal><H2 text={"WE DON'T *hire* SPACES.\nWE *own* THEM."} className="text-[#ebc754] whitespace-pre-line" /></Reveal>
      <Reveal delay={0.1}>
        {EXPERIENCE.paras.slice(2).map((p) => <p key={p} className="mt-8 mx-auto max-w-[34rem] text-[#f2e5de] text-base md:text-lg leading-[1.7]">{p}</p>)}
        <Link to="/stays" className={`${btn.yellow} mt-12`} data-testid="experience-book-stay">{EXPERIENCE.stayCta}</Link>
      </Reveal>
    </div>
  </section>
);

const PANEL_IMG = { events: "/skin/panel-events.webp", stays: "/skin/panel-stays.webp", venue: "/skin/panel-venue.webp", unbound: "/skin/panel-unbound.webp" };
export const Panels = () => (
  <section id="pathways" className="grid grid-cols-2 lg:grid-cols-4 scroll-mt-24" data-testid="section-pathways">
    {PATHWAYS.map((p) => (
      <SmartLink key={p.key} to={p.to} className="group relative aspect-[3/4] lg:aspect-[5/8] overflow-hidden" data-testid={`pathway-card-${p.key}`}>
        <img src={PANEL_IMG[p.key]} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#231f20]/85 via-[#231f20]/20 to-transparent transition-colors duration-700 lg:group-hover:via-[#231f20]/50" />
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
          <h3 className="feral-display text-[#f2e5de] text-xl sm:text-2xl lg:text-[2rem] leading-[1.02]">{p.title}</h3>
          <p className="hidden sm:block mt-3 text-[#f2e5de]/90 text-sm md:text-base leading-relaxed max-w-sm lg:max-h-0 lg:opacity-0 lg:overflow-hidden lg:group-hover:max-h-40 lg:group-hover:opacity-100 transition-[max-height,opacity] duration-700" data-testid={`pathway-copy-${p.key}`}>{p.copy}</p>
          <span className="mt-4 md:mt-6 inline-flex items-center gap-2 feral-nav uppercase tracking-[0.06em] text-[11px] md:text-xs text-[#f2e5de]">{p.cta} <ArrowRight size={13} /></span>
        </div>
      </SmartLink>
    ))}
  </section>
);

export const WhatsOn = () => {
  const [events, setEvents] = useState(null);
  useEffect(() => { axios.get(`${API}/events`).then((r) => setEvents(r.data || [])).catch(() => setEvents([])); }, []);
  return (
    <section id="whats-on" className="bg-[#f2e5de] py-24 md:py-32 px-6 lg:px-[3.75rem] scroll-mt-24" data-testid="section-whats-on">
      <div className="relative max-w-[1500px] mx-auto text-center">
        <img src="/skin/tfc-yellow.svg" alt="The FERAL Collective" className="hidden md:block absolute right-0 -top-6 w-32 lg:w-40 animate-[spin_40s_linear_infinite]" />
        <Reveal>
          <H2 text={"WHAT'S *on.*"} className="text-[#ebc754]" />
          <p className="mt-6 mx-auto max-w-[24rem] text-[#231f20] text-base md:text-lg leading-[1.6]">{WHATS_ON.copy}</p>
          <Link to="/events" className={`${btn.rustOutline} mt-8 !h-[48px]`} data-testid="whats-on-see-all">{WHATS_ON.cta}</Link>
        </Reveal>
        <div className="mt-16 md:mt-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10 text-left" data-testid="whats-on-events-grid">
          {(events || []).slice(0, 3).map((ev, i) => (
            <Reveal key={ev.slug} delay={i * 0.1}>
              <Link to={`/events/${ev.slug}`} className="group relative block aspect-[4/5] overflow-hidden bg-[#2c6052]" data-testid={`whats-on-card-${ev.slug}`}>
                {((ev.images || [])[0] || ev.cardImage) && <img src={(ev.images || [])[0] || ev.cardImage} alt={ev.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />}
                <div className="absolute inset-0 bg-gradient-to-t from-[#231f20]/85 via-[#231f20]/10 to-transparent" />
                <span className="absolute top-4 left-4 feral-nav uppercase tracking-[0.06em] text-[10px] bg-[#ebc754] text-[#9b3d0a] px-3 py-1.5">{ev.invitationOnly ? "Invitation only" : springText(ev, ev.tag || "Event")}</span>
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 text-[#f2e5de]">
                  {ev.dateLine && <p className="feral-nav uppercase tracking-[0.06em] text-[11px]">{ev.dateLine}</p>}
                  <h3 className="feral-display text-2xl md:text-[1.9rem] leading-tight mt-1">{ev.name}</h3>
                  {ev.venue && <p className="feral-nav uppercase tracking-[0.06em] text-[11px] mt-1 opacity-90">{ev.venue}</p>}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Island = () => (
  <section className="relative overflow-hidden py-32 md:py-48 px-6 text-center" data-testid="section-anglesey">
    <img src="/home/anglesey-sea.webp" alt="The Anglesey coast" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    <div className="absolute inset-0 bg-[#231f20]/45" />
    <Reveal className="relative max-w-[38rem] mx-auto">
      <p className="feral-nav uppercase tracking-[0.08em] text-xs text-[#f2e5de] mb-6">{ANGLESEY.eyebrow}</p>
      <H2 text={"AN ISLAND MADE\nFOR *slowing down.*"} className="text-[#ebc754] whitespace-pre-line" />
      <p className="mt-8 text-[#f2e5de] text-base md:text-lg leading-[1.7]">{ANGLESEY.copy}</p>
    </Reveal>
  </section>
);

export const WhyFeral = () => (
  <section id="why-feral" className="grid md:grid-cols-2 scroll-mt-24" data-testid="section-founder">
    <div className="bg-[#2c6052] px-6 md:px-14 lg:px-24 py-20 md:py-0 flex flex-col justify-center order-2 md:order-1">
      <Reveal>
        <H2 text={"*Why* FERAL EXISTS."} className="text-[#ebc754]" />
        <p className="mt-8 text-[#f2e5de] text-base md:text-lg leading-[1.7] max-w-[30rem]">{FOUNDER.copy}</p>
        <Link to="/our-story" className={`${btn.yellow} mt-10 !h-[48px] !px-7 !text-[12px]`} data-testid="founder-teaser-cta">{FOUNDER.cta}</Link>
      </Reveal>
    </div>
    <div className="relative aspect-[4/5] md:aspect-auto md:min-h-[44rem] order-1 md:order-2">
      <img src="/skin/tom.webp" alt="Tom, FERAL founder" className="absolute inset-0 w-full h-full object-cover object-top" loading="lazy" />
    </div>
  </section>
);

export const Pause = ({ id = "breathwork", source = "homepage", copy = BREATHWORK.copy }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) { setState("invalid"); return; }
    setState("sending");
    try { await axios.post(`${API}/breathwork`, { email, source }); setState("done"); } catch { setState("error"); }
  };
  return (
    <section id={id} className="grid md:grid-cols-2 scroll-mt-24" data-testid="section-breathwork">
      <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[44rem]">
        <img src="/skin/pause.webp" alt="A guest resting during a FERAL meditation" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="bg-[#9b3d0a] px-6 md:px-14 lg:px-24 py-20 md:py-0 flex flex-col justify-center">
        <Reveal>
          <H2 text={"TAKE A *pause*\nWITH US."} className="text-[#f2e5de] whitespace-pre-line" />
          <p className="mt-8 text-[#f2e5de] text-base md:text-lg leading-[1.7] max-w-[30rem]">{copy}</p>
          {state === "done" ? (
            <p className="mt-10 flex items-center gap-3 text-[#ebc754]" data-testid="breathwork-success"><Check size={20} /> You're on the list. Your session is on its way soon.</p>
          ) : (
            <form noValidate onSubmit={submit} className="mt-10 flex flex-col sm:flex-row sm:items-end gap-5 max-w-[34rem]" data-testid="breathwork-form">
              <label htmlFor={`${id}-email`} className="sr-only">Email address</label>
              <input id={`${id}-email`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" data-testid="breathwork-email-input"
                className="flex-1 bg-transparent border-b border-[#ebc754] outline-none py-3 text-[#f2e5de] placeholder:text-[#ebc754]/80 text-sm" />
              <button disabled={state === "sending"} data-testid="breathwork-submit-button" className={`${btn.yellow} !h-[46px] !px-6 !text-[12px] disabled:opacity-60`}>{state === "sending" ? "Sending…" : BREATHWORK.button}</button>
            </form>
          )}
          {state === "invalid" && <p className="mt-3 text-sm text-[#ebc754]" data-testid="breathwork-error">Please enter a valid email address.</p>}
          {state === "error" && <p className="mt-3 text-sm text-[#ebc754]" data-testid="breathwork-error">Something went wrong — please try again.</p>}
        </Reveal>
      </div>
    </section>
  );
};

const GALLERY = [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => `/skin/gallery-${n}.webp`);
export const Gallery = () => {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % GALLERY.length), 5000); return () => clearInterval(t); }, []);
  return (
    <section className="relative h-[70vh] md:h-[100vh] max-h-[1000px] min-h-[420px] overflow-hidden bg-[#231f20]" data-testid="home-gallery">
      {GALLERY.map((src, n) => (
        <img key={src} src={src} alt="A FERAL gathering" loading={n ? "lazy" : "eager"} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${n === i ? "opacity-100" : "opacity-0"}`} />
      ))}
      <div className="absolute bottom-6 inset-x-0 flex justify-center gap-2.5">
        {GALLERY.map((src, n) => (
          <button key={src} onClick={() => setI(n)} aria-label={`Photo ${n + 1}`} data-testid={`gallery-dot-${n}`} className={`w-2.5 h-2.5 rounded-full transition-colors ${n === i ? "bg-[#f2e5de]" : "bg-[#f2e5de]/45"}`} />
        ))}
      </div>
    </section>
  );
};
